import { useEffect, useRef } from "react";
import { trackAdminEvent } from "../../../../services/AdminTelemetryService";

const DEBOUNCE_MS = 300;
const TEXT_MAX = 50;

function getElementLabel(el: Element | null): string | null {
	if (!el) return null;
	const track = el.getAttribute?.("data-track");
	if (track) return track.slice(0, TEXT_MAX);
	const text = (el.textContent || "").trim().replace(/\s+/g, " ");
	return text ? text.slice(0, TEXT_MAX) : null;
}

function findSection(el: Element | null): string | null {
	let cur: Element | null = el;
	while (cur) {
		const s = cur.getAttribute?.("data-track-section");
		if (s) return s.slice(0, 120);
		cur = cur.parentElement;
	}
	return null;
}

/**
 * Delegated click tracking (debounced per target fingerprint).
 */
export function useClickTracking(): void {
	const lastFireRef = useRef<Map<string, number>>(new Map());

	useEffect(() => {
		const onClick = (event: MouseEvent) => {
			const target = event.target as Element | null;
			if (!target || typeof target.closest !== "function") return;

			const el = target.closest("button, a, [role='button'], [data-track]") as HTMLElement | null;
			if (!el) return;

			const href = el.tagName === "A" ? (el as HTMLAnchorElement).href || null : null;
			const section = findSection(el);
			const label = getElementLabel(el);
			const explicit = el.getAttribute("data-track");

			const fp = `${el.tagName}|${href || ""}|${explicit || label || ""}`;
			const now = Date.now();
			const last = lastFireRef.current.get(fp) || 0;
			if (now - last < DEBOUNCE_MS) return;
			lastFireRef.current.set(fp, now);
			if (lastFireRef.current.size > 200) {
				lastFireRef.current.clear();
			}

			void trackAdminEvent({
				eventType: "user_interaction",
				level: "info",
				action: "click",
				metadata: {
					elementTag: el.tagName,
					elementText: label,
					href: href ? href.slice(0, 500) : null,
					section,
					dataTrack: explicit,
					targetId: el.id || null,
				},
			});
		};

		document.addEventListener("click", onClick, true);
		return () => document.removeEventListener("click", onClick, true);
	}, []);
}
